import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { apiClient } from '../../utils/apiClient';
import { generateSlug } from '../../utils/slugUtils';
import { renderContent } from '../renderContent';
import CourseBlockRenderer, { BLOCK_CONFIG } from '../admin/CourseBlockRenderer';
import QuizSession from './QuizSession';
import FlashcardSession from './FlashcardSession';
import Modal from '../common/Modal';
import CourseNavigationSidebar from './CourseNavigationSidebar';
import { Headphones } from 'lucide-react';


const IFRAME_CONFIG = { ADD_TAGS: ['iframe'], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'src', 'title'] };
const API_BASE = process.env.REACT_APP_API_BASE_URL;

function sortByNotionNumber([, a], [, b]) {
    const na = (a[0]?.notion_number ?? '').split('.').map(Number);
    const nb = (b[0]?.notion_number ?? '').split('.').map(Number);
    for (let i = 0; i < Math.max(na.length, nb.length); i++) {
        const d = (na[i] ?? 0) - (nb[i] ?? 0);
        if (d !== 0) return d;
    }
    return 0;
}


function LazyImage({ src, alt, className }) {
    const [loaded, setLoaded] = useState(false);
    return (
        <div className="relative">
            {!loaded && <div className="absolute inset-0 bg-gray-100 dark:bg-white/[0.05] rounded-2xl animate-pulse" />}
            <img
                src={src}
                alt={alt}
                loading="lazy"
                onLoad={() => setLoaded(true)}
                className={`${className} transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            />
        </div>
    );
}

function KnowledgeCircle({ score, size = 40 }) {
    const r = (size - 6) / 2;
    const circ = 2 * Math.PI * r;
    const fill = circ - (score / 100) * circ;
    const color = score >= 80 ? '#22c55e' : score >= 50 ? '#3b82f6' : score >= 20 ? '#f59e0b' : '#9ca3af';
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0" aria-label={`Score : ${score}%`}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth="3" className="dark:stroke-gray-700" />
            {score > 0 && (
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="3"
                    strokeDasharray={circ} strokeDashoffset={fill}
                    strokeLinecap="round"
                    style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%', transition: 'stroke-dashoffset 0.6s ease' }}
                />
            )}
            <text x={size / 2} y={size / 2 + 4} textAnchor="middle" fontSize="10" fontWeight="600"
                fill={score > 0 ? color : '#9ca3af'}>
                {score}%
            </text>
        </svg>
    );
}

function ReviewTags({ reviews, isQuiz }) {
    if (!reviews?.length) return <span className="text-xs text-gray-400">Aucune révision</span>;
    return (
        <div className="flex gap-1 flex-wrap">
            {reviews.map((rev, i) => {
                const ok = isQuiz ? rev.rating >= 2 : rev.rating >= 1;
                const mid = !isQuiz && rev.rating === 1;
                return (
                    <span key={i} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${ok ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' :
                        mid ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                            'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                    }`}>
                        {rev.date}
                    </span>
                );
            })}
        </div>
    );
}

function CourseSection({ section, renderSectionActions }) {
    const type = section.content_type || 'text';
    const cfg = BLOCK_CONFIG[type];

    const renderInner = () => {
        if (cfg) {
            const blockMarkdown = `:::${type}${section.title ? ' ' + section.title : ''}\n${section.content || ''}\n:::`;
            return <CourseBlockRenderer content={blockMarkdown} />;
        }

        if (type === 'image_prompt') return null;

        if (type === 'heading') {
            const prefix = section._headingPrefix;
            const isH3 = section._isH3;
            return (
                <div className={`flex items-baseline gap-2 ${isH3 ? 'mt-3 ml-4' : 'mt-6'}`}>
                    <span className={`font-mono shrink-0 text-gray-400 dark:text-gray-500 select-none ${isH3 ? 'text-xs' : 'text-sm'}`}>{prefix}</span>
                    <span className={`font-bold text-gray-900 dark:text-white leading-snug ${isH3 ? 'text-base' : 'text-lg'} [&_p]:inline [&_p]:m-0`}>{renderContent(section.content || '')}</span>
                </div>
            );
        }

        if (type === 'image') return (
            <div className="my-4">
                {section.title && <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 italic [&_p]:inline [&_p]:m-0">{renderContent(section.title)}</p>}
                {section.media_url
                    ? <LazyImage src={section.media_url} alt={section.media_description || ''} className="rounded-2xl w-full max-w-3xl mx-auto shadow-md" />
                    : <span className="text-xs text-gray-400">Image non disponible</span>}
            </div>
        );

        if (type === 'video' && section.media_url) return (
            <div className="my-4">
                {section.title && <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 italic [&_p]:inline [&_p]:m-0">{renderContent(section.title)}</p>}
                <div className="aspect-video w-full max-w-3xl mx-auto overflow-hidden rounded-2xl shadow-lg bg-black">
                    <video controls className="w-full h-full object-cover" src={section.media_url} />
                </div>
            </div>
        );

        if (type === 'podcast') return (
            <div className="bg-cyan-500/10 border border-cyan-500/30 p-6 rounded-2xl flex flex-col items-center shadow-inner">
                <Headphones className="w-10 h-10 text-cyan-600 dark:text-cyan-400 mb-3 drop-shadow-md" />
                <h3 className="text-lg font-bold text-cyan-800 dark:text-cyan-300 mb-4 text-center [&_p]:inline [&_p]:m-0">{renderContent(section.title)}</h3>
                <audio controls className="w-full max-w-md rounded-full">
                    <source src={section.content} />
                    Ton navigateur ne supporte pas l'élément audio.
                </audio>
            </div>
        );

        if (type === 'video') return (
            <div className="my-6">
                <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100 [&_p]:inline [&_p]:m-0">{renderContent(section.title)}</h3>
                <div className="aspect-video w-full max-w-3xl mx-auto overflow-hidden rounded-2xl shadow-lg border border-gray-200 dark:border-white/[0.05] bg-black">
                    {section.content.includes('<iframe') ? (
                        <div className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.content, IFRAME_CONFIG) }} />
                    ) : (
                        <video controls className="w-full h-full object-cover">
                            <source src={section.content} />
                            Ton navigateur ne supporte pas la vidéo.
                        </video>
                    )}
                </div>
            </div>
        );

        if (type === 'html') return (
            <div>
                <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100 [&_p]:inline [&_p]:m-0">{renderContent(section.title)}</h3>
                <div className="text-gray-700 dark:text-gray-300 leading-relaxed text-base space-y-4 bg-gray-50 dark:bg-black/20 p-6 rounded-2xl border border-gray-100 dark:border-white/[0.02]"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.content) }} />
            </div>
        );

        if (type === 'ai_draft') return (
            <div><CourseBlockRenderer content={section.content} /></div>
        );

        return (
            <div>
                {section.title && <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100 [&_p]:inline [&_p]:m-0">{renderContent(section.title)}</h3>}
                <div className="leading-relaxed text-base space-y-4">{renderContent(section.content)}</div>
            </div>
        );
    };

    const inner = renderInner();
    if (!inner) return null;

    return (
        <div id={`section-${section.id}`} className="group relative">
            {inner}
            {renderSectionActions && renderSectionActions(section)}
        </div>
    );
}

function NotionItemsPanel({ notionId, user }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});
    const [practiceItem, setPracticeItem] = useState(null); // { item, isQuiz }

    useEffect(() => {
        if (!practiceItem) return;
        const h = e => { if (e.key === 'Escape') setPracticeItem(null); };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [practiceItem]);

    useEffect(() => {
        setLoading(true);
        apiClient(`${API_BASE}/get_notion_items.php?notion_id=${notionId}`)
            .then(r => r.json())
            .then(d => { if (d.success) setData(d); })
            .finally(() => setLoading(false));
    }, [notionId]);

    const toggle = id => setExpanded(p => ({ ...p, [id]: !p[id] }));

    if (loading) return <div className="py-4 text-center text-sm text-gray-400">Chargement…</div>;
    if (!data) return null;

    const { flashcards, quizzes } = data;
    if (!flashcards.length && !quizzes.length)
        return <p className="text-sm text-gray-400 py-2">Aucune flashcard ni quiz pour cette notion.</p>;

    const ItemRow = ({ item, isQuiz }) => {
        const open = !!expanded[`${isQuiz ? 'q' : 'f'}${item.id}`];
        const rowKey = `${isQuiz ? 'q' : 'f'}${item.id}`;
        const rate = item.interval_days != null
            ? Math.round(Math.log(Math.min(Math.max(item.interval_days, 1), 30)) / Math.log(30) * 100)
            : null;
        const quizType = isQuiz ? (item.answer?.type ?? 'single_choice') : null;

        const renderAnswer = () => {
            if (!isQuiz) {
                return (
                    <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Réponse</p>
                        <div className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">{renderContent(item.answer)}</div>
                    </div>
                );
            }
            if (quizType === 'ordering') {
                return (
                    <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Ordre correct</p>
                        <ol className="space-y-1">
                            {(item.answer.items ?? []).map((it, i) => (
                                <li key={i} className="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
                                    <span className="shrink-0 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-bold flex items-center justify-center text-[10px]">{i + 1}</span>
                                    <span className="[&_p]:inline [&_p]:m-0 pt-0.5">{renderContent(it)}</span>
                                </li>
                            ))}
                        </ol>
                    </div>
                );
            }
            if (quizType === 'matching') {
                return (
                    <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Associations</p>
                        <ul className="space-y-1">
                            {(item.answer.pairs ?? []).map((pair, i) => (
                                <li key={i} className="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
                                    <span className="[&_p]:inline [&_p]:m-0 font-medium">{renderContent(pair.left)}</span>
                                    <span className="text-gray-400 shrink-0">→</span>
                                    <span className="[&_p]:inline [&_p]:m-0">{renderContent(pair.right)}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            }
            if (quizType === 'fill_in_the_blank') {
                return (
                    <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Texte à compléter</p>
                        <div className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed mb-2">
                            {renderContent(item.answer.text)}
                        </div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Réponses</p>
                        <div className="flex flex-wrap gap-1.5">
                            {(item.answer.blanks ?? []).map((b, i) => (
                                <span key={i} className="text-xs px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 font-medium border border-emerald-200 dark:border-emerald-800/30">
                                    {renderContent(b)}
                                </span>
                            ))}
                        </div>
                    </div>
                );
            }
            const correctOptions = item.answer?.options?.filter(o => o.isCorrect) ?? [];
            return (
                <>
                    <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Réponse</p>
                        <div className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                            {correctOptions.map(o => o.text).join(', ') || '—'}
                        </div>
                    </div>
                    {item.answer?.options?.length > correctOptions.length && (
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Options</p>
                            <ul className="space-y-1">
                                {item.answer.options.map((opt, i) => (
                                    <li key={i} className={`text-xs px-2 py-1 rounded flex items-start gap-1.5 ${opt.isCorrect
                                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                                        : 'text-gray-500 dark:text-gray-500'
                                    }`}>
                                        <span className="shrink-0 font-bold">{opt.isCorrect ? '✓' : '✗'}</span>
                                        <span className="[&_p]:inline [&_p]:m-0">{renderContent(opt.text)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            );
        };

        return (
            <div className="border border-gray-200 dark:border-white/[0.06] rounded-xl overflow-hidden">
                <div className="flex items-start">
                    <button
                        onClick={() => toggle(rowKey)}
                        className="flex-1 flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors min-w-0"
                    >
                        <span className={`mt-0.5 shrink-0 w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold ${isQuiz ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400'
                            : 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                        }`}>{isQuiz ? 'Q' : 'F'}</span>
                        <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 leading-snug [&_p]:inline [&_p]:m-0 line-clamp-2 min-w-0">
                            {renderContent(item.question)}
                        </span>
                        <div className="shrink-0 flex flex-col items-end gap-1 ml-2">
                            {rate !== null && (
                                <span className={`text-xs font-bold ${rate >= 70 ? 'text-emerald-500' : rate >= 40 ? 'text-amber-500' : 'text-red-400'}`}>
                                    {rate}%
                                </span>
                            )}
                            {item.interval_days !== null && (
                                <span className="text-[10px] text-gray-400">J+{Math.round(item.interval_days)}</span>
                            )}
                        </div>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                            className={`w-4 h-4 shrink-0 text-gray-400 transition-transform mt-0.5 ${open ? 'rotate-180' : ''}`}>
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>
                    <button
                        onClick={() => setPracticeItem({ item, isQuiz })}
                        title="S'auto-évaluer"
                        className="shrink-0 self-stretch px-3 flex items-center text-fuchsia-400 hover:text-fuchsia-600 dark:hover:text-fuchsia-300 hover:bg-fuchsia-50 dark:hover:bg-fuchsia-900/10 transition-colors border-l border-gray-100 dark:border-white/[0.04]"
                    >
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                            <polygon points="5 3 19 12 5 21 5 3" />
                        </svg>
                    </button>
                </div>
                {open && (
                    <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-white/[0.04] space-y-3 bg-gray-50/50 dark:bg-white/[0.01]">
                        {renderAnswer()}
                        <div className="flex items-start gap-3">
                            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold shrink-0 pt-0.5">Historique</span>
                            <ReviewTags reviews={item.last_reviews} isQuiz={isQuiz} />
                            {item.total_reviews > 0 && (
                                <span className="text-[10px] text-gray-400 ml-auto">{item.total_reviews} révision{item.total_reviews > 1 ? 's' : ''}</span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="mt-6 space-y-4">
            {flashcards.length > 0 && (
                <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400 mb-2">
                        Flashcards ({flashcards.length})
                    </p>
                    <div className="space-y-1.5">
                        {flashcards.map(fc => <ItemRow key={fc.id} item={fc} isQuiz={false} />)}
                    </div>
                </div>
            )}
            {quizzes.length > 0 && (
                <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-cyan-600 dark:text-cyan-400 mb-2">
                        Quiz ({quizzes.length})
                    </p>
                    <div className="space-y-1.5">
                        {quizzes.map(qz => <ItemRow key={qz.id} item={qz} isQuiz={true} />)}
                    </div>
                </div>
            )}

            {practiceItem && (
                <Modal
                    isOpen={true}
                    onClose={() => setPracticeItem(null)}
                    title={practiceItem.isQuiz ? "Auto-évaluation : Quiz" : "Auto-évaluation : Flashcards"}
                    size="lg"
                >
                    <div className="overflow-hidden">
                        {practiceItem.isQuiz ? (
                            <QuizSession
                                quizzes={[practiceItem.item]}
                                user={user}
                                onExit={() => setPracticeItem(null)}
                                preview={true}
                            />
                        ) : (
                            <FlashcardSession
                                cards={[practiceItem.item]}
                                user={user}
                                onExit={() => setPracticeItem(null)}
                                preview={true}
                            />
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
}

export default function CourseContentDisplay({
    courseContent,
    selectedLevel,
    user,
    lockedNotions,
    masteryThreshold,
    notionScores,
    chapters,
    loadChapter,
    openItemsNotionId,
    setOpenItemsNotionId,
    renderNotionHeaderActions,
    renderSectionActions,

    // Navigation state and props
    sidebarEntries = [],
    activeNotionId,
    setActiveNotionId,
    mainScrollEl
}) {
    const [showMobileSummary, setShowMobileSummary] = useState(false);
    const sidebarNavRef = useRef(null);

    // Scroll automatique de la sidebar pour garder l'entrée active visible
    useEffect(() => {
        if (!activeNotionId || !sidebarNavRef.current) return;
        const activeBtn = sidebarNavRef.current.querySelector(`[data-sidebar-id="${activeNotionId}"]`);
        if (!activeBtn) return;
        const nav = sidebarNavRef.current;
        const btnTop = activeBtn.offsetTop;
        const btnBottom = btnTop + activeBtn.offsetHeight;
        const navScrollTop = nav.scrollTop;
        const navHeight = nav.clientHeight;
        if (btnTop < navScrollTop + 32) {
            nav.scrollTo({ top: btnTop - 32, behavior: 'smooth' });
        } else if (btnBottom > navScrollTop + navHeight - 32) {
            nav.scrollTo({ top: btnBottom - navHeight + 32, behavior: 'smooth' });
        }
    }, [activeNotionId]);

    const filteredNotions = Object.entries(courseContent || {})
        .sort(sortByNotionNumber)
        .filter(([, sections]) => {
            if (selectedLevel === null) return true;
            return sections[0]?.level_number === selectedLevel || sections[0]?.level_number === String(selectedLevel);
        });

    return (
        <div className="flex gap-8 items-start w-full">
            {/* ── Sidebar de navigation (desktop uniquement) ── */}
            {sidebarEntries.length > 0 && (
                <nav ref={sidebarNavRef} className="hidden md:block w-52 shrink-0 sticky top-8 max-h-[calc(100vh-6rem)] overflow-y-auto">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3 px-1">
                        Dans ce chapitre
                    </p>
                    <CourseNavigationSidebar
                        sidebarEntries={sidebarEntries}
                        activeNotionId={activeNotionId}
                        setActiveNotionId={setActiveNotionId}
                        mainScrollEl={mainScrollEl}
                    />
                    <button
                        onClick={() => mainScrollEl.current?.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="mt-4 w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.04] transition-all"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 shrink-0">
                            <path fillRule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z" clipRule="evenodd" />
                        </svg>
                        Retour en haut
                    </button>
                </nav>
            )}

            {/* ── Affichage du contenu du cours ── */}
            <div className="flex-1 min-w-0 space-y-12">
                {filteredNotions.map(([notionName, sections], idx) => {
                    const firstSection = sections[0];
                    const notionId = firstSection ? (firstSection.notion_id || firstSection.category_id || firstSection.id) : null;
                    const blockingPrereqs = notionId ? (lockedNotions[notionId] || null) : null;
                    const isLocked = !!blockingPrereqs;

                    return (
                        <div
                            key={idx}
                            id={notionId ? `notion-${notionId}` : `notion-section-${idx}`}
                            data-notion-id={notionId || ''}
                            className="bg-white/70 dark:bg-white/[0.02] backdrop-blur-xl border border-gray-200 dark:border-white/[0.05] rounded-3xl p-8 lg:p-10 shadow-[0_0_30px_rgba(59,130,246,0.05)]"
                        >
                            <h2
                                id={generateSlug(firstSection?.notion_name || notionName)}
                                className="text-2xl font-bold mb-8 text-fuchsia-700 dark:text-fuchsia-400 border-b border-gray-200 dark:border-gray-700 pb-4 flex items-center gap-3"
                            >
                                <span className="flex-1">{notionName.replace(/^\d+(\.\d+)?\s+/, '')}</span>
                                {renderNotionHeaderActions ? renderNotionHeaderActions(notionName, firstSection, isLocked) : (
                                    notionId && !isLocked && <KnowledgeCircle score={notionScores[notionId] ?? 0} />
                                )}
                                {isLocked && (
                                    <span className="text-sm font-normal px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-700/40 flex items-center gap-1">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                        </svg>
                                        Verrouillée
                                    </span>
                                )}
                            </h2>

                            {isLocked ? (
                                <div className="flex flex-col items-center py-8 text-center gap-4">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 text-amber-400">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                    <div>
                                        <p className="text-gray-700 dark:text-gray-300 font-semibold mb-1">Notion verrouillée</p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            Atteins <span className="font-bold text-amber-600 dark:text-amber-400">{masteryThreshold}% de maîtrise</span> sur {blockingPrereqs.length > 1 ? 'ces notions' : 'cette notion'} pour la débloquer :
                                        </p>
                                    </div>
                                    <ul className="space-y-1 w-full max-w-sm">
                                        {blockingPrereqs.map((p, i) => {
                                            const targetChapter = chapters.find(c => c.name === p.chapter_name);
                                            return (
                                                <li key={i}>
                                                    <button
                                                        onClick={() => targetChapter && loadChapter(targetChapter)}
                                                        disabled={!targetChapter}
                                                        className={`w-full text-sm px-4 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 text-left transition-all ${targetChapter ? 'hover:bg-amber-100 dark:hover:bg-amber-900/20 hover:border-amber-300 dark:hover:border-amber-700 cursor-pointer group' : 'cursor-default'}`}
                                                    >
                                                        <span className="font-semibold text-amber-800 dark:text-amber-300 [&_p]:inline [&_p]:m-0">{renderContent(p.notion_name)}</span>
                                                        <span className="flex items-center justify-between mt-0.5">
                                                            <span className="text-xs text-amber-600 dark:text-emerald-500">{p.chapter_name}</span>
                                                            {targetChapter && <span className="text-xs text-amber-500 dark:text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity">Aller au chapitre →</span>}
                                                        </span>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {(() => {
                                        let h2 = 0;
                                        let h3 = 0;
                                        const sectionsWithNumbers = sections.map(section => {
                                            const type = section.content_type || 'text';
                                            if (type === 'heading') {
                                                const isH3 = /^#{3,}\s/.test(section.content || '');
                                                if (isH3) {
                                                    h3++;
                                                } else {
                                                    h2++;
                                                    h3 = 0;
                                                }
                                                const prefix = isH3 ? `${h2}.${h3}` : `${h2}`;
                                                return { ...section, _headingPrefix: prefix, _isH3: isH3 };
                                            }
                                            return section;
                                        });

                                        return sectionsWithNumbers.map(section => (
                                            <CourseSection
                                                key={section.id}
                                                section={section}
                                                renderSectionActions={renderSectionActions}
                                            />
                                        ));
                                    })()}

                                    {notionId && (
                                        <div className="mt-6 pt-5 border-t border-gray-100 dark:border-white/[0.05]">
                                            <button
                                                onClick={() => setOpenItemsNotionId(openItemsNotionId === notionId ? null : notionId)}
                                                className="flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-fuchsia-600 dark:hover:text-fuchsia-400 transition-colors"
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                                                    <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
                                                </svg>
                                                Flashcards &amp; Quiz
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                                    className={`w-3.5 h-3.5 transition-transform ${openItemsNotionId === notionId ? 'rotate-180' : ''}`}>
                                                    <polyline points="6 9 12 15 18 9" />
                                                </svg>
                                            </button>
                                            {openItemsNotionId === notionId && (
                                                <NotionItemsPanel
                                                    notionId={notionId}
                                                    user={user}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* ── Bouton sommaire mobile ── */}
            {sidebarEntries.length > 0 && (
                <button
                    onClick={() => setShowMobileSummary(true)}
                    className="md:hidden fixed bottom-20 left-6 z-[60] flex items-center gap-2 px-3 py-2 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg text-xs font-medium text-gray-600 dark:text-gray-300 hover:border-blue-300 dark:hover:border-cyan-700 transition-all"
                >
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 5A.75.75 0 012.75 9h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 9.75zm0 5a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75a.75.75 0 01-.75-.75z" clipRule="evenodd" />
                    </svg>
                    Sommaire
                </button>
            )}

            {/* ── Drawer sommaire mobile ── */}
            {showMobileSummary && (
                <div className="md:hidden fixed inset-0 z-[200] flex flex-col justify-end">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setShowMobileSummary(false)} />
                    <div className="relative bg-white dark:bg-gray-900 rounded-t-3xl p-6 max-h-[70vh] overflow-y-auto shadow-2xl">
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">Dans ce chapitre</p>
                            <button onClick={() => setShowMobileSummary(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                                <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5"><path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" /></svg>
                            </button>
                        </div>
                        <CourseNavigationSidebar
                            sidebarEntries={sidebarEntries}
                            activeNotionId={activeNotionId}
                            setActiveNotionId={setActiveNotionId}
                            mainScrollEl={mainScrollEl}
                            onItemClick={() => setShowMobileSummary(false)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
